import { Button, Colors, Input, TagChip } from '@cognite/cogs.js';
import React, { useState } from 'react';
import styled from 'styled-components/macro';

export const LabelListEdit = ({
  labels,
  onChange,
  disabled,
}: {
  labels: string[];
  onChange: (labels: string[]) => void;
  disabled: boolean;
}) => {
  const [labelInput, setLabelInput] = useState('');
  const [showLabelInput, setShowLabelInput] = useState(false);

  const hasInvalidLabelInput =
    !labelInput.trim() ||
    labels.some(
      (label) => label.toLowerCase() === labelInput.trim().toLowerCase()
    );

  const addLabel = () => {
    if (hasInvalidLabelInput || disabled) return;
    const newLabel = labelInput.trim();
    onChange([...labels, newLabel]);
    setLabelInput('');
    setShowLabelInput(false);
  };

  const removeLabel = (_e: React.MouseEvent<HTMLElement>, index?: number) => {
    if (index === undefined || disabled) return;
    const newLabelsState = [...labels];
    newLabelsState.splice(index, 1);
    onChange(newLabelsState);
  };

  const onLabelInputClose = () => {
    setLabelInput('');
    setShowLabelInput(false);
  };

  const onLabelInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addLabel();
    }
  };

  return (
    <StyledLabelList>
      {labels.map((label, index) => (
        <StyledTag
          label={label}
          size="small"
          icon="Close"
          aria-label={`Remove label ${label}`}
          onClick={(e) => removeLabel(e as any, index)}
          key={`label-list-${label}`}
        />
      ))}
      {showLabelInput ? (
        <StyledInputContainer
          error={!!labelInput.length && hasInvalidLabelInput}
        >
          <StyledInput
            className="inputLabel"
            autoFocus
            size="small"
            placeholder="Type label name"
            value={labelInput}
            onBlur={addLabel}
            onChange={(e) => setLabelInput(e.currentTarget.value)}
            onKeyPress={onLabelInputKeyPress}
            postfix={
              <StyledInputPostfixButton
                icon="Checkmark"
                disabled={hasInvalidLabelInput}
                title="Submit"
                size="small"
                type="primary"
                onClick={addLabel}
                aria-label="Submit"
              />
            }
            prefix={
              <StyledInputPrefixButton
                icon="Close"
                title="Close input"
                type="secondary"
                size="small"
                onClick={onLabelInputClose}
                aria-label="Close"
              />
            }
          />
        </StyledInputContainer>
      ) : (
        <StyledButtonAdd
          type="secondary"
          onClick={() => setShowLabelInput(true)}
          icon="Add"
          title="Add label"
          disabled={disabled}
          aria-label="Add label"
        />
      )}
    </StyledLabelList>
  );
};

const StyledLabelList = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
`;

// Tag missing in v9 → TagChip fallback for visual label
const StyledTag = styled(TagChip)`
  /* Replaced Colors tokens for COGS v9 */
  color: ${Colors['text-icon--interactive--default']} !important;
  /* Replaced Colors tokens for COGS v9 */
  background: ${Colors['surface--interactive--toggled-default']} !important;
  border-color: transparent !important;
  border-radius: 5px;
  height: 28px;
  overflow: hidden;
  position: relative;
  padding-right: 1.25rem;
  span {
    max-width: 100%;
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledInputPostfixButton = styled(Button)`
  width: 28px;
  height: 28px;
  border-radius: 0 !important;
`;

const StyledInputContainer = styled.div<{ error: boolean }>`
  /* Replaced Colors token for COGS v9 */
  border: 1px solid
    ${({ error }) =>
      error
        ? `${Colors['decorative--red--500']}`
        : 'var(--cogs-border-default)'};
  border-radius: 5px;
  overflow: hidden;
  display: inline-flex;
`;

const StyledInput = styled(Input)`
  border: 0;
  box-sizing: border-box;
  height: 28px;
  .cogs-input-wrapper {
    display: flex;
    justify-content: center;
    align-items: stretch;
    height: 28px;
  }
  input {
    height: 100%;
    padding: 2px 5px !important;
    max-width: 150px;
    border-color: transparent;
    transition: none;
  }
`;

const StyledInputPrefixButton = styled(Button)`
  width: 28px;
  height: 28px;
  border-radius: 0 !important;
  background: transparent;
  .cogs-icon {
    width: 15px !important;
    height: 15px !important;
    /* Replaced Colors token for COGS v9 */
    color: ${Colors['text-icon--interactive--default']};
  }
`;

const StyledButtonAdd = styled(Button)`
  width: 28px;
  height: 28px;
  padding: 6px !important;
`;
